import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Composer,
  DateSeparator,
  EmptyState,
  MessageList,
  MessageSkeleton,
  Panel,
  SendIcon,
} from 'armxlib'
import { PageMeta } from '../../app/PageMeta'
import { Shell } from '../../app/Shell'
import { useToast } from '../../app/toast'
import { en } from '../../i18n/en'
import { ApiError } from '../../lib/api'
import { dayLabel, formatDateTime, isSameDay } from '../../lib/format'
import { MAX_MESSAGE_LENGTH } from '../../lib/rules'
import { useMe } from '../account/hooks'
import type { Message } from './api'
import { BanDialog, DeleteDialog, ReportDialog } from './dialogs'
import { useLiveMessages, useMessages, useSendMessage } from './hooks'
import { MessageRow } from './MessageRow'
import type { MessageAction } from './MessageRow'

const t = en.chat
const SKELETON_WIDTHS = ['62%', '85%', '40%', '74%', '55%', '90%']
/** Distance from the bottom under which the page follows new messages. */
const FOLLOW_THRESHOLD = 160

/**
 * Keeps the page scrolled like a chat: opens on the latest message, follows new
 * ones while the reader is at the bottom (always for their own), and stays on
 * the same message when older ones are loaded above.
 * @param messages - The loaded messages, oldest first.
 * @param viewerId - Id of the reader, whose own messages are always followed.
 */
function useChatScroll(messages: Message[], viewerId: string | undefined) {
  const atBottom = useRef(true)
  const previous = useRef<{ oldest?: number; newest?: number; height: number }>({ height: 0 })

  useEffect(() => {
    const onScroll = () => {
      const root = document.documentElement
      atBottom.current = window.innerHeight + window.scrollY >= root.scrollHeight - FOLLOW_THRESHOLD
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useLayoutEffect(() => {
    const root = document.documentElement
    const oldest = messages[0]?.id
    const newest = messages.at(-1)
    const before = previous.current

    if (before.newest === undefined) {
      window.scrollTo(0, root.scrollHeight)
    } else if (oldest !== before.oldest && newest?.id === before.newest) {
      window.scrollBy(0, root.scrollHeight - before.height)
    } else if (newest && newest.id !== before.newest && (atBottom.current || newest.author?.id === viewerId)) {
      window.scrollTo(0, root.scrollHeight)
    }
    previous.current = { oldest, newest: newest?.id, height: root.scrollHeight }
  }, [messages, viewerId])
}

/** `/`: the public room, live. */
export function ChatPage() {
  const me = useMe()
  const messages = useMessages()
  useLiveMessages()
  const send = useSendMessage()
  const toast = useToast()
  const [draft, setDraft] = useState('')
  const [pending, setPending] = useState<{ action: MessageAction; message: Message } | null>(null)

  const list = messages.data ?? []
  const viewerId = me.data?.id
  useChatScroll(list, viewerId)

  const ban = me.data?.ban
  const banReason = ban ? t.banned(ban.until ? formatDateTime(ban.until) : null, ban.reason ?? null) : undefined

  // The field empties at once, so the next message can be typed while this one
  // is on its way; a refused message comes back if the field is still empty.
  function submit() {
    const content = draft.trim()
    if (!content) return
    setDraft('')
    send.mutate(content, {
      onError: (error) => {
        setDraft((current) => (current === '' ? content : current))
        if (error instanceof ApiError && error.code === 'banned') return
        toast(error instanceof ApiError ? en.errors[error.code] : en.errors.internal, 'danger')
      },
    })
  }

  function pick(action: MessageAction | undefined) {
    return pending && pending.action === action ? pending.message : null
  }

  return (
    <Shell
      bottomBar={
        <Composer
          value={draft}
          onChange={setDraft}
          onSubmit={submit}
          maxLength={MAX_MESSAGE_LENGTH}
          placeholder={t.placeholder}
          label={t.placeholder}
          sending={send.isPending}
          disabled={Boolean(ban)}
          disabledReason={banReason}
        />
      }
    >
      <PageMeta title={t.title} />
      <Panel title={t.room} description={t.roomDescription}>
        {messages.isPending ? (
          <MessageList label={t.room}>
            {SKELETON_WIDTHS.map((width) => (
              <MessageSkeleton key={width} avatar width={width} />
            ))}
          </MessageList>
        ) : messages.isError ? (
          <Alert tone="danger" title={t.loadFailed}>
            <Button compact onClick={() => void messages.refetch()}>
              {en.common.tryAgain}
            </Button>
          </Alert>
        ) : list.length === 0 ? (
          <EmptyState icon={<SendIcon />} title={t.emptyTitle} description={t.emptyDescription} />
        ) : (
          <MessageList
            label={t.room}
            hasOlder={messages.hasNextPage}
            loadingOlder={messages.isFetchingNextPage}
            onLoadOlder={() => void messages.fetchNextPage()}
            loadOlderLabel={t.loadOlder}
          >
            {list.map((message, index) => {
              const previous = list[index - 1]
              return (
                <Fragment key={message.id}>
                  {(!previous || !isSameDay(previous.created_at, message.created_at)) && (
                    <DateSeparator>{dayLabel(message.created_at, t)}</DateSeparator>
                  )}
                  <MessageRow
                    message={message}
                    viewerId={viewerId}
                    moderator={me.data?.moderator ?? false}
                    onAction={(action, target) => setPending({ action, message: target })}
                  />
                </Fragment>
              )
            })}
          </MessageList>
        )}
      </Panel>

      <ReportDialog message={pick('report')} onClose={() => setPending(null)} />
      <DeleteDialog message={pick('delete')} onClose={() => setPending(null)} />
      <BanDialog user={pick('ban')?.author ?? null} onClose={() => setPending(null)} />
    </Shell>
  )
}
